import i18n from '../../config/i18n';
import type { CartItem } from '../../contexts/CartContext';

export interface ServiceUsage {
  service_id: number;
  service_name: string;
  total: number;
  used: number;
  remaining: number;
}

export interface CheckoutForfait {
  user_forfait_id: number;
  forfait_id: number;
  forfait_name: string;
  description: string;
  status: string;
  remaining_sessions: number;
  purchase_date: string;
  pro_id: number;
  services?: ServiceUsage[];
}

export interface CheckoutPaymentCalculations {
  usedByItem: Record<string, 'credits' | 'forfait' | 'none'>;
  forfaitIdByItem: Record<string, number | null>;
  remainingCreditsByPro: Record<number, number>;
  totalCreditsUsed: number;
  totalOnSiteEuro: number;
  totalOnlineEuro: number;
  totalForfaitsUsedCount: number;
  /** Forfait/crédits du panier, bénéfices pas encore chargés — ne pas encaisser. */
  walletPending: boolean;
}

interface CGVInfoLike {
  version_id?: number;
}

export interface BatchBookingPayload {
  pro_id: number;
  service_id: number;
  date?: string;
  start_time?: string;
  animal_id: number;
  payment_method: 'credits' | 'forfait' | 'stripe' | 'classic';
  use_forfait_id?: number;
  creneau_id?: number;
  user_id: number;
  client_answer?: string;
  location_id?: number;
  employee_id?: number;
  service_address?: string;
  is_online_payment: boolean;
  cgv_accepted: boolean;
  cgv_version_id?: number;
  travel_fee_eur?: number;
  distance_km?: number;
  total_price_eur?: number;
}

export function calculateCheckoutPayments(
  items: CartItem[],
  useCreditsFlags: Record<string, boolean>,
  useForfaitFlags: Record<string, number | null>,
  userCredits: Record<number, number>,
  userForfaits: CheckoutForfait[],
  walletKnown: { forfaits?: boolean; credits?: boolean } = {}
): CheckoutPaymentCalculations {
  const remainingCreditsByPro = { ...userCredits };
  const remainingForfaits = JSON.parse(JSON.stringify(userForfaits)) as CheckoutForfait[];

  const usedByItem: Record<string, 'credits' | 'forfait' | 'none'> = {};
  const forfaitIdByItem: Record<string, number | null> = {};

  let totalCreditsUsed = 0;
  let totalOnSiteEuro = 0;
  let totalOnlineEuro = 0;
  let totalForfaitsUsedCount = 0;
  let walletPending = false;

  items.forEach(item => {
    const forfaitFlag = useForfaitFlags[item.cartItemId];
    const chosenForfaitId =
      forfaitFlag === null
        ? null
        : forfaitFlag ??
          (item.payment_method === 'forfait'
            ? remainingForfaits.find(
                f =>
                  f.pro_id === item.proId &&
                  f.services?.some(s => s.service_id === item.serviceId && s.remaining > 0)
              )?.user_forfait_id
            : undefined);
    if (chosenForfaitId) {
      const forfait = remainingForfaits.find(
        f => f.user_forfait_id === chosenForfaitId && f.pro_id === item.proId
      );
      const serviceUsage = forfait?.services?.find(s => s.service_id === item.serviceId);

      if (serviceUsage && serviceUsage.remaining > 0) {
        usedByItem[item.cartItemId] = 'forfait';
        forfaitIdByItem[item.cartItemId] = chosenForfaitId;
        serviceUsage.remaining -= 1;
        totalForfaitsUsedCount += 1;
        return;
      }
    }

    if (
      forfaitFlag !== null &&
      item.payment_method === 'forfait' &&
      remainingForfaits.length === 0
    ) {
      if (walletKnown.forfaits) {
        // Portefeuille chargé, plus de séances — encaisser en euros.
      } else {
        usedByItem[item.cartItemId] = 'forfait';
        totalForfaitsUsedCount += 1;
        walletPending = true;
        return;
      }
    }

    const isCreditDisabled =
      item.creditPrice === null ||
      (item.creditPrice !== undefined && Number(item.creditPrice) === 0);
    const wantsToUseCredits =
      useCreditsFlags[item.cartItemId] === true ||
      (useCreditsFlags[item.cartItemId] !== false && item.payment_method === 'credits');
    const proRemaining = remainingCreditsByPro[item.proId] || 0;
    const itemCreditCost =
      item.creditPrice !== undefined && item.creditPrice !== null
        ? Number(item.creditPrice)
        : Number(item.price);

    if (!isCreditDisabled && wantsToUseCredits && proRemaining >= itemCreditCost) {
      usedByItem[item.cartItemId] = 'credits';
      remainingCreditsByPro[item.proId] -= itemCreditCost;
      totalCreditsUsed += itemCreditCost;
      return;
    }

    if (
      wantsToUseCredits &&
      item.payment_method === 'credits' &&
      Object.keys(remainingCreditsByPro).length === 0
    ) {
      if (walletKnown.credits) {
        // Solde crédits connu (0) — encaisser en euros.
      } else {
        usedByItem[item.cartItemId] = 'credits';
        walletPending = true;
        return;
      }
    }

    usedByItem[item.cartItemId] = 'none';
    if (itemChargesOnline(item)) {
      totalOnlineEuro += item.price;
    } else {
      totalOnSiteEuro += item.price;
    }
  });

  return {
    usedByItem,
    forfaitIdByItem,
    remainingCreditsByPro,
    totalCreditsUsed,
    totalOnSiteEuro,
    totalOnlineEuro,
    totalForfaitsUsedCount,
    walletPending,
  };
}

/** #45 — online only force Stripe ; avec choix client, honor classic/stripe. */
export function itemChargesOnline(item: CartItem): boolean {
  if (!item.allowOnlinePayment) return false;
  if (item.allowClientPaymentChoice) {
    return item.payment_method === 'stripe';
  }
  return true;
}

function resolvePaymentMethod(
  item: CartItem,
  paymentCalculations: CheckoutPaymentCalculations
): BatchBookingPayload['payment_method'] {
  const usage = paymentCalculations.usedByItem[item.cartItemId];
  if (usage === 'credits') return 'credits';
  if (usage === 'forfait') return 'forfait';
  if (item.payment_method === 'stripe') return 'stripe';
  if (item.payment_method === 'classic') {
    if (!item.allowOnlinePayment || item.allowClientPaymentChoice) {
      return 'classic';
    }
    // Online only (pas de choix) : force stripe — anti-régression #45
    return 'stripe';
  }
  return item.allowOnlinePayment ? 'stripe' : 'classic';
}

export function buildBatchBookingPayload(params: {
  items: CartItem[];
  itemAnimals: Record<string, number>;
  paymentCalculations: CheckoutPaymentCalculations;
  cgvAccepted: Record<number, boolean>;
  cgvInfos: Record<number, CGVInfoLike | null>;
  userId: number;
}): BatchBookingPayload[] {
  const { items, itemAnimals, paymentCalculations, cgvAccepted, cgvInfos, userId } = params;

  return items
    .filter(item => item.serviceId)
    .map(item => {
      const animalId = itemAnimals[item.cartItemId];
      if (animalId == null) {
        throw new Error(i18n.t('booking:checkout.selectCompanion'));
      }

      if (!item.sessionId && (!item.date || !item.time)) {
        throw new Error(i18n.t('booking:checkout.missingDateOrTime'));
      }

      const paymentMethod = resolvePaymentMethod(item, paymentCalculations);
      const cgvInfo = cgvInfos[item.proId];

      return {
        pro_id: item.proId,
        service_id: item.serviceId as number,
        date: item.date,
        start_time: item.time ? item.time.substring(0, 5) : undefined,
        animal_id: animalId,
        payment_method: paymentMethod,
        use_forfait_id: paymentCalculations.forfaitIdByItem[item.cartItemId] || undefined,
        creneau_id: item.sessionId,
        user_id: userId,
        client_answer: item.clientAnswer,
        location_id: item.locationId,
        employee_id: item.employeeId,
        service_address: item.service_address,
        client_phone: item.client_phone,
        is_online_payment: paymentMethod === 'stripe',
        cgv_accepted: cgvAccepted[item.proId] || false,
        cgv_version_id: cgvInfo?.version_id,
        travel_fee_eur: item.mileageData?.travel_fee_eur,
        distance_km: item.mileageData?.distance_km,
        total_price_eur: item.mileageData ? item.price : undefined,
      };
    });
}

export function checkoutDueEuro(calcs: CheckoutPaymentCalculations): number {
  return calcs.totalOnlineEuro + calcs.totalOnSiteEuro;
}

export type CheckoutSummaryTotal =
  | { kind: 'forfait' }
  | { kind: 'credits'; credits: number }
  | { kind: 'euro'; amount: number };

/** Total affiché : Inclus / crédits si le cadeau couvre tout, sinon le reste en euros. */
export function checkoutSummaryTotal(calcs: CheckoutPaymentCalculations): CheckoutSummaryTotal {
  const due = checkoutDueEuro(calcs);
  if (due > 0) return { kind: 'euro', amount: due };
  if (calcs.totalCreditsUsed > 0 && calcs.totalForfaitsUsedCount === 0) {
    return { kind: 'credits', credits: calcs.totalCreditsUsed };
  }
  if (calcs.totalForfaitsUsedCount > 0 || calcs.totalCreditsUsed > 0) {
    return { kind: 'forfait' };
  }
  return { kind: 'euro', amount: 0 };
}

/** Forfait / crédits déjà choisis (panier ou calcul checkout). */
export function cartItemWalletKind(
  item: Pick<CartItem, 'cartItemId' | 'payment_method'>,
  usedByItem?: Record<string, 'credits' | 'forfait' | 'none'>
): 'forfait' | 'credits' | null {
  const used = usedByItem?.[item.cartItemId];
  if (used === 'forfait' || used === 'credits') return used;
  if (used === 'none') return null;
  if (item.payment_method === 'forfait' || item.payment_method === 'credits') {
    return item.payment_method;
  }
  return null;
}

/** Total du tiroir panier : forfait / crédits du cadeau, sinon euros restants. */
export function cartItemsSummaryTotal(
  items: Array<Pick<CartItem, 'price' | 'payment_method' | 'creditPrice'>>
): CheckoutSummaryTotal {
  let due = 0;
  let forfaitCount = 0;
  let credits = 0;
  for (const item of items) {
    if (item.payment_method === 'forfait') {
      forfaitCount += 1;
    } else if (item.payment_method === 'credits') {
      credits += Number(item.creditPrice || 0);
    } else {
      due += item.price;
    }
  }
  if (due > 0) return { kind: 'euro', amount: due };
  if (credits > 0 && forfaitCount === 0) return { kind: 'credits', credits };
  if (forfaitCount > 0 || credits > 0) return { kind: 'forfait' };
  return { kind: 'euro', amount: 0 };
}

/** Après réclamation d’un cadeau : pré-sélectionne le forfait sur les items éligibles. */
export function forfaitFlagsForCartItems(
  items: CartItem[],
  forfaits: CheckoutForfait[]
): Record<string, number> {
  const flags: Record<string, number> = {};
  for (const item of items) {
    if (item.forfaitId || item.packId) continue;
    const eligible = forfaits.find(
      f =>
        f.pro_id === item.proId &&
        f.services?.some(s => s.service_id === item.serviceId && s.remaining > 0)
    );
    if (eligible) {
      flags[item.cartItemId] = eligible.user_forfait_id;
    }
  }
  return flags;
}

/** Après un pack cadeau : pré-sélectionne les crédits sur les items éligibles. */
export function creditFlagsForCartItems(
  items: CartItem[],
  userCredits: Record<number, number>,
  forfaitFlags: Record<string, number | null> = {}
): Record<string, boolean> {
  const remaining = { ...userCredits };
  const flags: Record<string, boolean> = {};
  for (const item of items) {
    if (item.forfaitId || item.packId || forfaitFlags[item.cartItemId]) continue;
    const isCreditDisabled =
      item.creditPrice === null ||
      (item.creditPrice !== undefined && Number(item.creditPrice) === 0);
    if (isCreditDisabled) continue;
    const cost =
      item.creditPrice !== undefined && item.creditPrice !== null
        ? Number(item.creditPrice)
        : Number(item.price);
    const proRemaining = remaining[item.proId] || 0;
    if (proRemaining >= cost) {
      flags[item.cartItemId] = true;
      remaining[item.proId] = proRemaining - cost;
    }
  }
  return flags;
}

/** Affichage panier : forfait/crédits du cadeau même si l’item a encore un tarif. */
/** Badge « Solde Pro » : masqué si le forfait couvre toutes les lignes (crédits pro non pertinents). */
export function shouldShowCheckoutProBalanceBadge(
  checkoutItems: Pick<CartItem, 'cartItemId' | 'proId'>[],
  userCredits: Record<number, number>,
  usedByItem: CheckoutPaymentCalculations['usedByItem']
): boolean {
  if (checkoutItems.length === 0) return false;
  const proId = checkoutItems[0].proId;
  if (userCredits[proId] === undefined) return false;
  const allPaidByForfait = checkoutItems.every(
    item => usedByItem[item.cartItemId] === 'forfait'
  );
  return !allPaidByForfait;
}

export function overlayGiftWalletOnCartItems(
  items: CartItem[],
  forfaits: CheckoutForfait[],
  creditsByPro: Record<number, number> = {},
  opts: { forfaitsKnown?: boolean; creditsKnown?: boolean } = {}
): CartItem[] {
  const forfaitFlags = forfaitFlagsForCartItems(items, forfaits);
  const creditFlags = creditFlagsForCartItems(items, creditsByPro, forfaitFlags);
  return items.map(item => {
    if (forfaitFlags[item.cartItemId]) {
      return { ...item, payment_method: 'forfait' as const };
    }
    if (creditFlags[item.cartItemId]) {
      return { ...item, payment_method: 'credits' as const };
    }
    const fallback: CartItem['payment_method'] = item.allowOnlinePayment ? 'stripe' : 'classic';
    if (opts.forfaitsKnown && item.payment_method === 'forfait') {
      return { ...item, payment_method: fallback };
    }
    if (opts.creditsKnown && item.payment_method === 'credits') {
      return { ...item, payment_method: fallback };
    }
    return item;
  });
}
